/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}

/**
 * Mirror Shopify newsletter signups into Klaviyo list W7MF8N.
 * Runs in addition to — not instead of — Shopify's own form POST,
 * so subscribers land in both Shopify customers and Klaviyo.
 */
(function () {
  const KLAVIYO_COMPANY_ID = 'Xz7hdm';
  const KLAVIYO_LIST_ID = 'W7MF8N';
  const KLAVIYO_SOURCE = 'Shopify Newsletter';

  /** @param {HTMLFormElement} form */
  function attach(form) {
    if (form.dataset.klaviyoBound === '1') return;
    form.dataset.klaviyoBound = '1';

    form.addEventListener('submit', function () {
      const emailInput = /** @type {HTMLInputElement | null} */ (
        form.querySelector('input[name="contact[email]"]')
      );
      const email = emailInput && emailInput.value && emailInput.value.trim();
      if (!email || email.indexOf('@') === -1) return;

      try {
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: KLAVIYO_SOURCE,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
              },
            },
          }),
        }).catch(function () {});
      } catch (e) {}
    });
  }

  function init() {
    document
      .querySelectorAll(/** @type {string} */ ('form.email-signup__form'))
      .forEach((el) => attach(/** @type {HTMLFormElement} */ (el)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();